import * as XLSX from "xlsx";
import { summaryRevenue } from "./posRevenueComparison";

export const exportToXlsx = (summary, startDate, endDate, orgs = [], separateByDate = false, paymentMethod = "all", t = (key) => key) => {
  const buildSheetData = (data) => {
    const revenueSum =
      parseFloat(data.cardAmount || 0) +
      parseFloat(data.cashAmount || 0) +
      parseFloat(data.bankTransferAmount || 0) +
      parseFloat(data.qrAmount || 0);

    const formatted = (data.products || []).map((product) => {
      const revenue = product.totalDue || 0;
      const totalPercentage = revenueSum ? (revenue / revenueSum) * 100 : 0;

      const row = {
        ...(separateByDate ? { Date: product.date } : {}),
        Product: product.title,
        "Price TTC": product.price,
        "Number Sold": product.quantity,
        "VAT IN": product.vat,
        "VAT OUT": product.vatOut,
        "% of Revenue": totalPercentage.toFixed(2),
        Revenue: revenue,
        "XVAT Revenue": Number((revenue / (1 + (product.vat || 0) / 100)).toFixed(2)) || 0,
        "Sold IN": product.quantityIn,
        "Revenue IN": product.totalIn,
        "Sold OUT": product.quantityOut,
        "Revenue OUT": product.totalOut,
      };
      return row;
    });

    const totalSold = formatted.reduce((sum, item) => sum + parseInt(item["Number Sold"]), 0);
    const totalRevenueSum = formatted.reduce((sum, item) => sum + parseFloat(item["Revenue"]), 0);
    const totalXvatRevenueSum = formatted.reduce(
      (sum, item) => sum + parseFloat(item["XVAT Revenue"] || 0),
      0,
    );
    const totalSoldIn = formatted.reduce((sum, item) => sum + parseInt(item["Sold IN"]), 0);
    const totalRevenueIn = formatted.reduce((sum, item) => sum + parseFloat(item["Revenue IN"]), 0);
    const totalSoldOut = formatted.reduce((sum, item) => sum + parseInt(item["Sold OUT"]), 0);
    const totalRevenueOut = formatted.reduce(
      (sum, item) => sum + parseFloat(item["Revenue OUT"]),
      0,
    );

    formatted.push({
      ...(separateByDate ? { Date: "" } : {}),
      Product: "All",
      "Price TTC": null,
      "Number Sold": totalSold,
      "VAT IN": null,
      "VAT OUT": null,
      "% of Revenue": "100.00",
      Revenue: totalRevenueSum,
      "XVAT Revenue": totalXvatRevenueSum,
      "Sold IN": totalSoldIn,
      "Revenue IN": totalRevenueIn,
      "Sold OUT": totalSoldOut,
      "Revenue OUT": totalRevenueOut,
    });

    return formatted;
  };

  const getVatTotals = (data) => {
    const totals = {};
    (data.products || []).forEach((product) => {
      const vat = product.vat || 0;
      const revenue = parseFloat(product.totalDue || 0);
      totals[vat] = (totals[vat] || 0) + revenue;
    });
    return totals;
  };

  const baseColumnWidths = [
    { wch: 30 },
    { wch: 15 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];
  const baseHeaders = [
    "Product",
    "Price TTC",
    "Number Sold",
    "VAT IN",
    "VAT OUT",
    "% of Revenue",
    "Revenue",
    "XVAT Revenue",
    "Sold IN",
    "Revenue IN",
    "Sold OUT",
    "Revenue OUT",
  ];

  const columnWidths = separateByDate ? [{ wch: 12 }, ...baseColumnWidths] : baseColumnWidths;
  const headers = separateByDate ? ["Date", ...baseHeaders] : baseHeaders;

  const createWorksheet = (data, vatTotals) => {
    const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
    worksheet["!cols"] = [...columnWidths, { wch: 5 }, { wch: 10 }, { wch: 15 }];
    headers.forEach((header, index) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: index })];
      if (cell) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "right" },
        };
      }
    });

    if (vatTotals && Object.keys(vatTotals).length) {
      const startCol = headers.length + 1;
      const vatRows = [
        ["VAT Totals"],
        ["VAT", "Revenue"],
        ...Object.entries(vatTotals).map(([vat, total]) => [vat, Number(total.toFixed(2))]),
      ];
      XLSX.utils.sheet_add_aoa(worksheet, vatRows, { origin: { r: 0, c: startCol } });

      const titleCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: startCol })];
      if (titleCell) {
        titleCell.s = { font: { bold: true } };
      }
      vatRows[1].forEach((_, i) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 1, c: startCol + i })];
        if (cell) {
          cell.s = {
            font: { bold: true },
            alignment: { horizontal: "right" },
          };
        }
      });
    }

    return worksheet;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const start = new Date(startDate.getTime());
  start.setHours(start.getHours());
  const end = new Date(endDate.getTime());


  const formattedStartDate = formatDate(start);
  const formattedEndDate = formatDate(end);

  const workbook = XLSX.utils.book_new();

  const paymentRevenue = summaryRevenue(summary, paymentMethod);
  const productsRevenue = Math.round((summary.products || []).reduce((sum, product) => sum + Math.round((Number(product.totalDue) || 0) * 100), 0)) / 100;
  const summaryRows = [
    [t("export_summary.period"), `${formattedStartDate} — ${formattedEndDate}`],
    [t("export_summary.organizations"), orgs.map((org) => org.organization || org.orgName || org.name || org.id).join(", ")],
    [t("export_summary.payment_method"), paymentMethod || "all"],
    [],
    [t("total_revenue"), paymentRevenue],
    ...["card", "cash", "qr", "bankTransfer", "cashless"].map((method) => [t(`export_summary.${method}`), Number(summary[`${method}Amount`]) || 0]),
    [],
    [t("export_summary.products_revenue"), productsRevenue],
    [t("export_summary.difference"), Math.round((paymentRevenue - productsRevenue) * 100) / 100],
    [t("export_summary.difference_help")],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet["!cols"] = [{ wch: 65 }, { wch: 45 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  const mainSheetData = buildSheetData(summary);
  const mainVatTotals = getVatTotals(summary);
  XLSX.utils.book_append_sheet(workbook, createWorksheet(mainSheetData, mainVatTotals), "Products");

  (orgs || []).forEach((org, idx) => {
    const sheetData = buildSheetData(org);
    const vatTotals = getVatTotals(org);
    const sheetName = org?.organization || org?.orgName || org?.name || `Org${idx + 1}`;
    XLSX.utils.book_append_sheet(
      workbook,
      createWorksheet(sheetData, vatTotals),
      sheetName.substring(0, 31),
    );
  });

  XLSX.writeFile(workbook, `Revenue_${formattedStartDate}_${formattedEndDate}.xlsx`);
  return workbook;
};

